import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { Actions as EMPLOYEEACTIONS } from "../../store/actions/employeeActions";
import { Actions as ACTIONS } from "../../store/actions/leaveActions";

import { copyToClipboard } from "@/helpers/ClipboardHelpers";
import Tooltip from "@/tooltip";
import { ClipboardIcon, ExclamationTriangleIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { format, isValid } from "date-fns";


export const EmployeeRow = ({
	userPermissions,
	multiplePaySchedules,
	departments,
	employee,
	setNewEmployeeModal,
	currency,
	getEmployeeDetails,
	isAdmin,
	deleteAgreement,
	clearEmployee,
	jurisdiction,
	hasDepartments,
	salaryVisibility,
	fromBulkChanges,
}) => {
	const currentSalary =
		employee?.salaries?.find(
			(s) =>
				s?.start == s?.end ||
				(s.start <= new Date().toISOString() &&
					(s.end >= new Date().toISOString() || s.end == employee.terminationDate)),
		) || employee.salaries?.[employee.salaries.length - 1];
	const currentTax =
		employee?.taxSettings?.find((s) => {
			s.start = s.start ? s.start : s.startDate;
			s.end = s.end ? s.end : s.endDate || "null";
			const datesEqual = s?.start == s?.end;
			const startBeforeToday = s.start <= new Date().toISOString();
			const endAfterToday = s.end >= new Date().toISOString();
			const terminated = s.end == employee.terminationDate;

			return datesEqual || (startBeforeToday && (endAfterToday || terminated));
		}) || employee.taxSettings?.[employee.taxSettings.length - 1];

	const doe = employee.engagementDate;
	const termination = employee.terminationDate;
	const incomplete = employee?.isIncomplete;
	const salaryAmount = currentSalary?.salaryAmount
		? parseFloat(currentSalary?.salaryAmount).toLocaleString(undefined, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			})
		: "";
	const periodName = currentSalary?.payPeriodFrequencyName;

	const getEmployeeStatus = () => {
		const hasPaySchedule = multiplePaySchedules ? currentSalary?.paySchedule : true;
		const hasTaxCode = currentTax
			? jurisdiction == "MT"
				? !!currentTax?.taxStatus || currentSalary?.employmentType == 1843110
				: !!currentTax?.taxCode
			: true;

		if (incomplete) return "Incomplete";
		if (!hasTaxCode) return "No Tax";
		if (!hasPaySchedule) return "No Pay Schedule";
		return "Edit Employee";
	};

	const ButtonElement = ({ employeeStatus }) => {
		switch (employeeStatus) {
			case "Incomplete":
				return (
					<Tooltip content={`Continue Adding Employee${employee.incompleteReason ? `. These fields are missing: ${employee.incompleteReason}` : ""}`}>
						<PencilSquareIcon
							className="h-5 w-5"
							style={{ marginTop: "3px" }}
						/>
					</Tooltip>
				);
			case "No Tax":
				return (
					<Tooltip content={`Missing ${jurisdiction == "MT" ? "Tax Rate" : "Tax Code"}`}>
						<ExclamationTriangleIcon
							className="h-5 w-5 text-white"
							style={{ marginTop: "3px" }}
						/>
					</Tooltip>
				);
			case "No Pay Schedule":
				return (
					<Tooltip content="Missing Payschedule">
						<ExclamationTriangleIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</Tooltip>
				);
			default:
				return (
					<Tooltip content="Edit Employee">
						<PencilSquareIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</Tooltip>
				);
		}
	};

	const ButtonManager = ({ buttonClass, onClick, link, hash }) => {
		const employeeStatus = getEmployeeStatus();

		switch (employeeStatus) {
			case "Incomplete":
				buttonClass = "btn-warning";
				link = "/organisation/people";
				onClick = () => {
					setNewEmployeeModal(true);
					getEmployeeDetails(employee.id);
				};
				break;
			case "No Tax":
				buttonClass = "btn-danger";
				hash = "Tax";
				break;
			case "No Pay Schedule":
				buttonClass = "btn-warning";
				hash = "Salary";
				break;
		}
		if (fromBulkChanges) return;
		return (
			<Link
				to={link || `/organisation/people/${employee.id}${hash ? `#${hash}` : ""}`}
				className={`btn btn-xs ${buttonClass || "btn-default"}`}
				type="button"
				style={{ marginLeft: 10, marginRight: 10 }}
				onClick={() => {
					clearEmployee();
					onClick && onClick();
				}}
			>
				<ButtonElement employeeStatus={employeeStatus} />
			</Link>
		);
	};

	return (
		<tr>
			<td>
				<ButtonManager />

				{isAdmin && !fromBulkChanges && (
					<div
						className="btn btn-xs btn-default"
						type="button"
						onClick={() => copyToClipboard(employee.id)}
						style={{ marginRight: 10 }}
					>
						<ClipboardIcon
							className="h-5 w-5 text-dark-gray"
							style={{ marginTop: "3px" }}
						/>
					</div>
				)}
			</td>
			<td>
				{userPermissions == "Read" || !incomplete ? (
					<Link
						to={`/organisation/people/${employee.id}`}
						onClick={() => clearEmployee()}
					>
						{employee.employeeName} {employee.employeeSurname}
					</Link>
				) : (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							clearEmployee();
							setNewEmployeeModal(true);
							getEmployeeDetails(employee.id);
						}}
					>
						{employee.employeeName} {employee.employeeSurname}
					</a>
				)}
			</td>
			<td>
				{isValid(new Date(doe)) && format(new Date(doe), "dd/MM/yyyy")}{" "}
				{termination ? `- ${format(new Date(termination), "dd/MM/yyyy")} ` : null}
			</td>
			<td>{employee.employmentTypeName}</td>
			{hasDepartments && <td>{departments?.find((dept) => dept.id == employee?.department)?.name}</td>}
			<td>
				{employee.roleName && employee.roleName != "null" && (
					<span>{typeof employee.roleName == "object" ? employee.roleName.name : employee.roleName}</span>
				)}
			</td>
			<td>
				{salaryAmount && (
					<span className={salaryVisibility ? "" : "blur"}>
						{currency}
						{salaryAmount}/{periodName}
					</span>
				)}
			</td>
			<td>
				{isAdmin && !fromBulkChanges && (
					<div
						className="btn btn-xs btn-danger"
						type="button"
						onClick={() => {
							deleteAgreement(employee.id);
						}}
					>
						<TrashIcon
							className="h-5 w-5"
							style={{ marginTop: "3px" }}
						/>
					</div>
				)}
			</td>
		</tr>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { employeeListWithSalary } = state.employees;

	const userPermissions = state?.permissions?.my?.permissions?.people;
	const { enableDepartments, paySchedules, departments, currency, jurisdiction } = state?.company?.settings;

	return {
		userPermissions,
		multiplePaySchedules: paySchedules?.length > 1,
		departments,
		employeeListWithSalary,
		currency,
		isAdmin: state?.my?.profile?.isAdmin,
		jurisdiction,
		hasDepartments: enableDepartments === undefined ? !!departments?.length : enableDepartments,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setNewEmployeeModal: () => dispatch(EMPLOYEEACTIONS.setNewEmployeeModal(true, true)),
	setOpenModal: (status, leave) => dispatch(ACTIONS.setOpenModal(status, leave)),
	saveLeave: (leave) => dispatch(ACTIONS.saveLeave(leave)),
	updatedEmployee: (response) => dispatch(EMPLOYEEACTIONS.updatedEmployee(true, response)),
	getEmployeeDetails: (agreementId) => dispatch(EMPLOYEEACTIONS.getEmployeeDetails(agreementId)),
	showCancelLeave: (leave, status) => dispatch(ACTIONS.showCancelLeave(leave, status)),
	showComments: (leave) => dispatch(ACTIONS.showComments(leave)),
	showAttachments: (leave) => dispatch(ACTIONS.showAttachments(leave)),
	deleteAgreement: (agreement) => dispatch(EMPLOYEEACTIONS.deleteAgreementModal(true, false, agreement)),
	clearEmployee: () => dispatch(EMPLOYEEACTIONS.clearEmployee()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeRow);
